import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Mail, Phone, MapPin } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const CONTACT_EMAIL = 'info@studioinsieme.it';

const buildContactMailto = (data) => {
  const subject = encodeURIComponent('Richiesta informazioni StudioInsieme');
  const body = encodeURIComponent([
    `Nome: ${data.name}`,
    `Email: ${data.email}`,
    `Telefono: ${data.phone || 'Non indicato'}`,
    '',
    'Messaggio:',
    data.message
  ].join('\n'));

  return `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
};

const Contatti = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        toast.success("Messaggio inviato! Ti contatteremo presto.");
        setFormData({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => navigate('/'), 2000);
      }
    } catch (error) {
      const contentType = error.response?.headers?.["content-type"] || "";
      const backendUnavailable =
        !error.response ||
        [404, 405].includes(error.response.status) ||
        contentType.includes("text/html");

      if (backendUnavailable) {
        window.location.href = buildContactMailto(formData);
        toast.info("Il modulo ha aperto la tua email. Se non si apre, scrivici a info@studioinsieme.it.");
        return;
      }

      toast.error(error.response?.data?.detail || "Si è verificato un errore. Riprova più tardi.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <section className="section-spacing">
        <div className="container">
          <div className="section-header">
            <h1 className="heading-1">Contattaci</h1>
            <p className="body-large text-secondary">
              Hai domande o vuoi maggiori informazioni? Siamo qui per aiutarti
            </p>
          </div>

          <div className="contact-grid">
            {/* Form di Contatto */}
            <Card className="contact-form-card">
              <CardHeader>
                <CardTitle className="heading-3">Invia un messaggio</CardTitle>
                <CardDescription>
                  Compila il modulo e ti risponderemo il prima possibile
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-group">
                    <Label htmlFor="name">Nome e Cognome *</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Mario Rossi"
                    />
                  </div>

                  <div className="form-group">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="mario.rossi@email.com"
                    />
                  </div>

                  <div className="form-group">
                    <Label htmlFor="phone">Telefono</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+39 333 1234567"
                    />
                  </div>

                  <div className="form-group">
                    <Label htmlFor="message">Messaggio *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Scrivi qui il tuo messaggio..."
                      rows={6}
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="btn-primary w-full" 
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Invio in corso...' : 'Invia messaggio'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Informazioni di Contatto */}
            <div className="contact-info-section">
              <Card className="contact-info-card">
                <CardHeader>
                  <CardTitle className="heading-3">Informazioni</CardTitle>
                  <CardDescription>
                    Altri modi per raggiungerci
                  </CardDescription>
                </CardHeader>
                <CardContent className="contact-info-list">
                  <div className="contact-info-item">
                    <div className="icon-wrapper">
                      <Mail className="contact-icon" />
                    </div>
                    <div>
                      <p className="body-small text-secondary">Email</p>
                      <p className="body-medium">info@studioinsieme.it</p>
                    </div>
                  </div>

                  <div className="contact-info-item">
                    <div className="icon-wrapper">
                      <Phone className="contact-icon" />
                    </div>
                    <div>
                      <p className="body-small text-secondary">Telefono</p>
                      <p className="body-medium">+39 333 123 4567</p>
                    </div>
                  </div>

                  <div className="contact-info-item">
                    <div className="icon-wrapper">
                      <MapPin className="contact-icon" />
                    </div>
                    <div>
                      <p className="body-small text-secondary">Sede</p>
                      <p className="body-medium">Online e in presenza</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="contact-hours-card">
                <CardHeader>
                  <CardTitle className="heading-3">Orari di disponibilità</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="hours-list">
                    <div className="hours-item">
                      <span className="body-medium">Lunedì - Venerdì</span>
                      <span className="body-medium">9:00 - 19:00</span>
                    </div>
                    <div className="hours-item">
                      <span className="body-medium">Sabato</span>
                      <span className="body-medium">9:00 - 13:00</span>
                    </div>
                    <div className="hours-item">
                      <span className="body-medium text-secondary">Domenica</span>
                      <span className="body-medium text-secondary">Chiuso</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contatti;
